import React, { useEffect, useState } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import Button, { Color } from "../../../../../../../../components/Form/Button";
import Input from "../../../../../../../../components/Form/Input";
import Select from "../../../../../../../../components/Form/Select";
import Modal from "../../../../../../../../components/Generic/Modal";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import useWarehouseContext from "../../../../../../../../hooks/useWarehouseContext";
import { useWarehousesMapsHook } from "../../../../../../../../hooks/useWarehouseMapActions";
import useYupValidation from "../../../../../../../../hooks/useYupValidation";
import { AxisConfig, AxisName, WarehouseMap } from "../../../../../../../../models/warehouse.map";
import { MapCell } from "../../../../../../../../models/warehouse.mapcell";
import {
  alphabeticIndex,
  AxisConfigOrdination,
  AxisConfigType,
  buildingMapCells,
  MatrixLengths,
  OverrideName,
} from "../../../../../../../../util/buildingMapCells";
import useStyles, {
  GridContainerTitle,
  MapGeneratedContainer,
  MapGeneratedWrapper,
} from "./styles";
import { WarehouseMapComponent } from "./WarehouseMapComponent";
import { SkeletonWrapper } from "./WarehouseMapComponent/styles";
import { WarehouseMapLocationModal } from "./WarehouseMapComponent/WarehouseMapLocationModal";

interface Props {
  open: boolean;
  hasError?: boolean;
  isCreatingNewMap?: boolean;
  isOnViewMode?: boolean;
  isLoading: boolean;
  closeModal: () => void;
  selectedWarehouseMap?: WarehouseMap;
  setHasChangedMapCell?: (hasChangedMapCell: boolean) => void;
  createWarehouseMap?: (warehouseMap: WarehouseMap) => void;
  updateWarehouseMap?: (warehouseMap: WarehouseMap, warehouseMapId: number) => void;
}

interface IHandleUpdateMapAxis {
  attributeSetState: React.Dispatch<React.SetStateAction<AxisConfig>>;
  attribute: string;
  value: any;
}

export const WarehouseMapPageModal = ({
  open,
  closeModal,
  hasError,
  selectedWarehouseMap,
  setHasChangedMapCell,
  isOnViewMode,
  createWarehouseMap,
  updateWarehouseMap,
  isLoading,
  isCreatingNewMap,
}: Props) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const {
    useUpdateWarehouseMapCell,
    useClearMapCell,
    setMapId,
    isLoadingMap,
    mapToUpdate,
    hasErrorMapCell,
  } = useWarehousesMapsHook();

  const { setSelectedMap } = useWarehouseContext();

  const warehouseMap = mapToUpdate || selectedWarehouseMap;

  const initialColumnConfig: AxisConfig = {
    initial: undefined,
    size: 0,
    ordination: AxisConfigOrdination.ASC,
    type: AxisConfigType.ALPHABETICAL,
    prefix: "",
    suffix: "",
  };

  const initialRowConfig: AxisConfig = {
    initial: undefined,
    size: 0,
    ordination: AxisConfigOrdination.ASC,
    type: AxisConfigType.NUMERIC,
    prefix: "",
    suffix: "",
  };

  const initialLevelConfig: AxisConfig = {
    initial: undefined,
    size: 0,
    ordination: AxisConfigOrdination.ASC,
    type: AxisConfigType.NUMERIC,
    prefix: "",
    suffix: "",
  };

  const initialMatrixLengths = {
    rowsLength: 1,
    columnsLength: 1,
    levelsLength: 1,
  };

  const [mapName, setMapName] = useState<string>("");
  const [mapDescription, setMapDescription] = useState<string>("");
  const [mapColumns, setMapColumns] = useState<AxisConfig>(
    selectedWarehouseMap?.configColumn || initialColumnConfig
  );
  const [mapRows, setMapRows] = useState<AxisConfig>(
    selectedWarehouseMap?.configRow || initialRowConfig
  );
  const [mapLevels, setMapLevels] = useState<AxisConfig>(
    selectedWarehouseMap?.configLevel || initialLevelConfig
  );

  const [matrixValues, setMatrixValues] = useState<OverrideName[]>([]);
  const [matrixLengths, setMatrixLengths] = useState<MatrixLengths>(initialMatrixLengths);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [showWarehouseMap, setShowWarehouseMap] = useState<boolean>(false);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [overrideNames, setOverrideNames] = useState<OverrideName[]>([]);
  const [hasOverride, setHasOverride] = useState<boolean>(false);
  const [hasChangedMapProperty, setHasChangedMapProperty] = useState<boolean>(false);

  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [mapCellSelected, setMapCellSelected] = useState<MapCell>();
  const [createdMapCells, setCreatedMapCells] = useState<MapCell[]>([]);
  const [isMapBuilded, setIsMapBuilded] = useState<boolean>(false);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { mutate: updateWarehouseMapCell, isLoading: isUpdating } = useUpdateWarehouseMapCell(
    (updatedMapCells) => {
      const updatedMapCellsArray = selectedWarehouseMap?.mapCells?.map((mapCell) => {
        const update = updatedMapCells.find((updatedMapCell) => updatedMapCell.id === mapCell.id);

        if (update) {
          return { ...mapCell, isActive: update.isActive };
        }

        return mapCell;
      });

      setSelectedMap({
        ...(selectedWarehouseMap || ({} as WarehouseMap)),
        mapCells: updatedMapCellsArray,
      });
    }
  );

  const { mutate: clearMapCell, isLoading: isClearing } = useClearMapCell();

  const getOverrideValues = (warehouseMap?: WarehouseMap) => {
    const overrideValues: OverrideName[] = [];

    if (mapRows && mapColumns && warehouseMap) {
      for (let row = 0; row < mapRows.size; row += 1) {
        const rowName = warehouseMap.mapCells?.find((mapCell) => mapCell.indexRow === row);

        overrideValues.push({
          axisName: AxisName.ROW,
          index: row,
          newName: rowName?.rowName || "",
        });

        for (let column = 0; column < mapColumns.size; column += 1) {
          const columnName = warehouseMap.mapCells?.find(
            (mapCell) => mapCell.indexColumn === column
          );

          overrideValues.push({
            axisName: AxisName.COLUMN,
            index: column,
            newName: columnName?.columnName || "",
          });
        }
      }
    }

    return overrideValues;
  };

  const handleUpdateMapCells = (currentMapCells: MapCell[], createdMapCells: MapCell[]) => {
    if (currentMapCells.length < 1 || createdMapCells.length < 1) return [];

    const updatedCells = createdMapCells.map((newMapCell) => {
      const existingCell = currentMapCells.find(
        (actualMapCell) =>
          actualMapCell.indexColumn === newMapCell.indexColumn &&
          actualMapCell.indexRow === newMapCell.indexRow &&
          actualMapCell.indexLevel === newMapCell.indexLevel
      );

      if (existingCell) {
        return { ...existingCell, ...newMapCell };
      }

      return newMapCell;
    });

    return updatedCells;
  };

  useEffect(() => {
    if (selectedWarehouseMap) {
      if (selectedWarehouseMap?.id && !isOnViewMode) {
        setMapId(selectedWarehouseMap?.id);
      }

      setMapName(selectedWarehouseMap?.name || "");
      setMapDescription(selectedWarehouseMap?.description || "");
      setMapColumns(selectedWarehouseMap?.configColumn || initialColumnConfig);
      setMapRows(selectedWarehouseMap?.configRow || initialRowConfig);
      setMapLevels(selectedWarehouseMap?.configLevel || initialLevelConfig);

      const overrideValues = getOverrideValues(selectedWarehouseMap);

      const building = buildingMapCells({
        configColumn: selectedWarehouseMap?.configColumn || initialColumnConfig,
        configRow: selectedWarehouseMap?.configRow || initialRowConfig,
        configLevel: selectedWarehouseMap?.configLevel || initialLevelConfig,
        overrideNames: overrideValues,
        previousMapCells: warehouseMap?.mapCells,
      });

      setCreatedMapCells(warehouseMap?.mapCells || []);
      setMatrixValues(building?.matrixValues);
      setMatrixLengths(building?.matrixLengths);
      setShowWarehouseMap(true);
    }
  }, [warehouseMap]);

  useEffect(() => {
    if (mapToUpdate) {
      const overrideValues = getOverrideValues(mapToUpdate);

      const building = buildingMapCells({
        configColumn: mapToUpdate?.configColumn || initialColumnConfig,
        configRow: mapToUpdate?.configRow || initialRowConfig,
        configLevel: mapToUpdate?.configLevel || initialLevelConfig,
        overrideNames: overrideValues,
        previousMapCells: warehouseMap?.mapCells,
      });

      setMatrixValues(building?.matrixValues);
      setMatrixLengths(building?.matrixLengths);
      setShowWarehouseMap(true);
    }
  }, [mapToUpdate, warehouseMap]);

  const handleChangeRowsType = (columnType: AxisConfigType) => {
    if (columnType === AxisConfigType.ALPHABETICAL) {
      setMapRows({ ...mapRows, type: AxisConfigType.NUMERIC, initial: undefined });
    }

    if (columnType === AxisConfigType.NUMERIC) {
      setMapRows({ ...mapRows, type: AxisConfigType.ALPHABETICAL, initial: undefined });
    }
  };

  const handleChangeColumnsType = (rowType: AxisConfigType) => {
    if (rowType === AxisConfigType.ALPHABETICAL) {
      setMapColumns({ ...mapColumns, type: AxisConfigType.NUMERIC, initial: undefined });
    }

    if (rowType === AxisConfigType.NUMERIC) {
      setMapColumns({ ...mapColumns, type: AxisConfigType.ALPHABETICAL, initial: undefined });
    }
  };

  const clearFields = () => {
    setMapName("");
    setMapDescription("");
    setMapColumns(initialColumnConfig);
    setMapRows(initialRowConfig);
    setMapLevels(initialLevelConfig);
  };

  const schema = yup.object().shape({
    mapName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    columnsQuantity: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .moreThan(0, translate("thisFieldMustBeGreaterThanZero"))
      .test(
        "is_greater_than_mapColumns",
        `${translate("youCantRemoveColumsTheMinimumIs")} ${
          selectedWarehouseMap?.configColumn && selectedWarehouseMap?.configColumn.size
        }.`,
        (value) => {
          if (
            value &&
            selectedWarehouseMap?.configColumn &&
            value < selectedWarehouseMap?.configColumn.size
          ) {
            return false;
          }

          return true;
        }
      ),
    rowsQuantity: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .moreThan(0, translate("thisFieldMustBeGreaterThanZero"))
      .test(
        "is_greater_than_mapColumns",
        `${translate("youCantRemoveColumsTheMinimumIs")} ${
          selectedWarehouseMap?.configRow && selectedWarehouseMap?.configRow.size
        }.`,
        (value) => {
          if (
            value &&
            selectedWarehouseMap?.configRow &&
            value < selectedWarehouseMap?.configRow.size
          ) {
            return false;
          }

          return true;
        }
      ),
    levelsQuantity: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .moreThan(0, translate("thisFieldMustBeGreaterThanZero"))
      .test(
        "is_greater_than_mapColumns",
        `${translate("youCantRemoveColumsTheMinimumIs")} ${
          selectedWarehouseMap?.configLevel && selectedWarehouseMap?.configLevel.size
        }.`,
        (value) => {
          if (
            value &&
            selectedWarehouseMap?.configLevel &&
            value < selectedWarehouseMap?.configLevel.size
          ) {
            return false;
          }

          return true;
        }
      ),
  });

  const warehouseMapFields = {
    mapName,
    columnsQuantity: mapColumns?.size,
    rowsQuantity: mapRows?.size,
    levelsQuantity: mapLevels?.size,
  };

  const isValid = () => {
    try {
      schema.validateSync(warehouseMapFields, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    let warehouseMapToSave = {} as WarehouseMap;

    const overrideValues = getOverrideValues(selectedWarehouseMap);

    const building = buildingMapCells({
      configColumn: mapColumns,
      configRow: mapRows,
      configLevel: mapLevels,
      overrideNames: hasOverride ? overrideNames : overrideValues,
      mapId: selectedWarehouseMap?.id,
      previousMapCells: warehouseMap?.mapCells,
    });

    const updatedMapCells = handleUpdateMapCells(
      warehouseMap?.mapCells || [],
      building?.mapCells || []
    );

    warehouseMapToSave = {
      ...selectedWarehouseMap,
      name: mapName,
      description: mapDescription,
      isActive: selectedWarehouseMap?.isActive ?? true,
      configColumn: mapColumns,
      configRow: mapRows,
      configLevel: mapLevels,
      mapCells: selectedWarehouseMap?.id ? updatedMapCells : createdMapCells,
    };

    if (isValid()) {
      if (selectedWarehouseMap && selectedWarehouseMap.id) {
        updateWarehouseMap?.(warehouseMapToSave, selectedWarehouseMap.id);
      } else {
        createWarehouseMap?.(warehouseMapToSave);
      }
    }
  };

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) isValid();
  }, [
    hasFormBeenSubmitted,
    ...Object.keys(warehouseMapFields).map(
      (key) => warehouseMapFields[key as keyof typeof warehouseMapFields]
    ),
  ]);

  const charactersUsed = [
    { name: translate("numbers"), value: AxisConfigType.NUMERIC },
    { name: translate("alphabetical"), value: AxisConfigType.ALPHABETICAL },
  ];

  const sequenceOrdination = [
    { name: translate("descendant"), value: AxisConfigOrdination.DESC },
    { name: translate("ascendant"), value: AxisConfigOrdination.ASC },
  ];

  const getLetter = (axisConfig: AxisConfig) => {
    const letters = Object.keys(alphabeticIndex);

    const letter = letters.filter((_, index) => index === axisConfig.initial)?.[0];

    return letter;
  };

  const handleSetInitial = (letter: string) => {
    let indexLetter: number | undefined;

    Object.keys(alphabeticIndex).forEach((alphabeticLetter, index) =>
      alphabeticLetter === letter?.toUpperCase() ? (indexLetter = index) : ""
    );

    return indexLetter;
  };

  const hadleUpdateMapAxis = ({ attributeSetState, attribute, value }: IHandleUpdateMapAxis) => {
    attributeSetState((state: AxisConfig) => ({ ...state, [attribute]: value }));
    setHasChangedMapProperty(true);
    setIsMapBuilded(false);
  };

  const getEditMapTitle = (selectedWarehouseMap: WarehouseMap) => {
    if (selectedWarehouseMap?.name) {
      return `${translate("edit")} "${selectedWarehouseMap?.name}"`;
    }

    return translate("editMap");
  };

  return (
    <Modal
      open={open}
      handleClose={() => {
        clearFields();
        closeModal();
      }}
      title={
        // eslint-disable-next-line no-nested-ternary
        isOnViewMode
          ? translate("viewMap")
          : selectedWarehouseMap
          ? getEditMapTitle(selectedWarehouseMap)
          : translate("createMap")
      }
      disableEnforceFocus
      data-cy="warehouseMapModal"
      dataCyTitle="warehouseMapTitle"
      showActionButtons
      className={classes.warehouseMapModal}
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        customColorCancel: Color.TEXT_INFO,
        dataCySave: "submitChangesButton",
        textSave: selectedWarehouseMap ? translate("save") : translate("create"),
        textCancel: isOnViewMode ? translate("close") : translate("cancel"),
        loading: isLoading,
        saveDisabled: isLoading || !isMapBuilded,
        onSave: !isOnViewMode ? () => handleSubmit() : undefined,
      }}
    >
      {selectedWarehouseMap?.id ? (
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item xs={12} md={3}>
            <Input
              data-cy="warehouseMapName"
              placeholder={translate("input")}
              label={translate("name")}
              required
              disabled={isOnViewMode}
              error={fieldHasError("mapName")}
              errorMessage={getErrorByField("mapName")}
              value={mapName || ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setMapName(event.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Input
              data-cy="warehouseMapDescription"
              placeholder={translate("input")}
              label={translate("description")}
              disabled={isOnViewMode}
              value={mapDescription || ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setMapDescription(event.target.value)
              }
            />
          </Grid>

          <Grid item xs={12}>
            <GridContainerTitle>{translate("mapSettings")}</GridContainerTitle>
          </Grid>
          <Grid item xs={12} md={3}>
            <Input
              data-cy="warehouseMapColumnQuantity"
              placeholder={translate("input")}
              label={translate("columnsQuantity")}
              required
              disabled={isOnViewMode}
              type="number"
              min={1}
              error={fieldHasError("columnsQuantity")}
              errorMessage={getErrorByField("columnsQuantity")}
              value={mapColumns.size}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                hadleUpdateMapAxis({
                  attributeSetState: setMapColumns,
                  attribute: "size",
                  value: Number(event.target.value),
                });
                setIsMapBuilded(false);
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Input
              data-cy="warehouseMapRowQuantity"
              placeholder={translate("input")}
              label={translate("rowsQuantity")}
              required
              disabled={isOnViewMode}
              type="number"
              min={1}
              error={fieldHasError("rowsQuantity")}
              errorMessage={getErrorByField("rowsQuantity")}
              value={mapRows.size}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                hadleUpdateMapAxis({
                  attributeSetState: setMapRows,
                  attribute: "size",
                  value: Number(event.target.value),
                });
                setIsMapBuilded(false);
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Input
              data-cy="warehouseMapLevelQuantity"
              placeholder={translate("input")}
              label={translate("levelsQuantity")}
              disabled={isOnViewMode}
              required
              type="number"
              min={1}
              error={fieldHasError("levelsQuantity")}
              errorMessage={getErrorByField("levelsQuantity")}
              value={mapLevels.size}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                hadleUpdateMapAxis({
                  attributeSetState: setMapLevels,
                  attribute: "size",
                  value: Number(event.target.value),
                });
                setIsMapBuilded(false);
              }}
            />
          </Grid>

          <MapGeneratedWrapper container item direction="column">
            <GridContainerTitle>{translate("mapGenerated")}</GridContainerTitle>
            {showWarehouseMap && isUpdating ? (
              <SkeletonWrapper>
                <SkeletonComponent isLoading={!isUpdating}>
                  <div />
                </SkeletonComponent>
              </SkeletonWrapper>
            ) : (
              <WarehouseMapComponent
                matrixLengths={matrixLengths}
                matrixValues={matrixValues}
                overrideNames={overrideNames}
                setHasOverride={setHasOverride}
                setOverrideNames={setOverrideNames}
                isViewMode={isOnViewMode}
                isCreate={false}
                mapCells={createdMapCells}
                handleClickMapCell={(mapCell: MapCell) => {
                  setMapCellSelected(mapCell);
                  setShowLocationModal(true);
                }}
                setHasChangedMapProperty={setHasChangedMapProperty}
              />
            )}
          </MapGeneratedWrapper>

          {hasChangedMapProperty && (
            <Button
              customColor={Color.SECONDARY}
              disabled={!mapColumns.size || !mapRows.size || !mapLevels.size || isOnViewMode}
              onClick={() => {
                if (isValid()) {
                  const overrideValues = getOverrideValues(selectedWarehouseMap);

                  const building = buildingMapCells({
                    configColumn: mapColumns,
                    configRow: mapRows,
                    configLevel: mapLevels,
                    overrideNames: hasOverride ? overrideNames : overrideValues,
                    previousMapCells: warehouseMap?.mapCells,
                  });

                  const updatedMapCells = handleUpdateMapCells(
                    warehouseMap?.mapCells || [],
                    building?.mapCells || []
                  );

                  setCreatedMapCells(updatedMapCells);
                  setMatrixValues(building?.matrixValues);
                  setMatrixLengths(building?.matrixLengths);
                  setShowWarehouseMap(true);
                  setHasChangedMapProperty(false);
                  setIsMapBuilded(true);
                }
              }}
            >
              {translate("buildMap")}
            </Button>
          )}

          {showLocationModal && (
            <WarehouseMapLocationModal
              open={showLocationModal}
              closeModal={() => setShowLocationModal(false)}
              mapSelected={warehouseMap}
              createdMapCells={createdMapCells}
              levelsSize={mapLevels.size}
              mapCellSelected={mapCellSelected}
              isLoading={isUpdating || isClearing || isLoadingMap}
              isOnViewMode={isOnViewMode}
              hasError={hasErrorMapCell}
              onUpdate={(mapCell) => {
                updateWarehouseMapCell(mapCell);
                setShowWarehouseMap(false);
                setHasChangedMapCell?.(true);
              }}
              onClear={(mapCellId) => {
                clearMapCell({ mapCellId });
                setShowWarehouseMap(false);
                setHasChangedMapCell?.(true);
              }}
            />
          )}
        </Grid>
      ) : (
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item xs={12} md={3}>
            <Input
              data-cy="warehouseMapName"
              placeholder={translate("input")}
              label={translate("name")}
              required
              disabled={isOnViewMode}
              error={fieldHasError("mapName")}
              errorMessage={getErrorByField("mapName")}
              value={mapName || ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setMapName(event.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Input
              data-cy="warehouseMapDescription"
              placeholder={translate("input")}
              label={translate("description")}
              disabled={isOnViewMode}
              value={mapDescription || ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setMapDescription(event.target.value)
              }
            />
          </Grid>

          <Grid container item direction="column" xs={6}>
            <Grid container item direction="row" spacing={2}>
              <Grid item xs={12}>
                <GridContainerTitle>{translate("columns")}</GridContainerTitle>
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  data-cy="warehouseMapColumnQuantity"
                  placeholder={translate("input")}
                  label={translate("columnsQuantity")}
                  required
                  disabled={isOnViewMode}
                  type="number"
                  min={1}
                  error={fieldHasError("columnsQuantity")}
                  errorMessage={getErrorByField("columnsQuantity")}
                  value={mapColumns.size}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapColumns,
                      attribute: "size",
                      value: Number(event.target.value),
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  data-cy="warehouseMapColumnCharacter"
                  placeholder={translate("select")}
                  label={translate("characterUsed")}
                  disabled={isOnViewMode}
                  value={mapColumns.type || ""}
                  onChange={(event: SelectChangeEvent<any>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapColumns,
                      attribute: "type",
                      value: event.target.value as AxisConfigType,
                    });
                    handleChangeRowsType(event.target.value as AxisConfigType);
                  }}
                >
                  {charactersUsed.map((character) => (
                    <MenuItem value={character.value} key={character.value}>
                      {character.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} md={2}>
                {mapColumns.type === AxisConfigType.ALPHABETICAL ? (
                  <Input
                    data-cy="warehouseMapColumnInitial"
                    placeholder={"A"}
                    disabled={isOnViewMode}
                    label={translate("initial")}
                    value={getLetter(mapColumns)}
                    type="text"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      hadleUpdateMapAxis({
                        attributeSetState: setMapColumns,
                        attribute: "initial",
                        value: handleSetInitial(event.target.value),
                      });
                    }}
                  />
                ) : (
                  <Input
                    data-cy="warehouseMapColumnInitial"
                    placeholder={"1"}
                    disabled={isOnViewMode}
                    label={translate("initial")}
                    value={mapColumns.initial}
                    type="number"
                    min={0}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      hadleUpdateMapAxis({
                        attributeSetState: setMapColumns,
                        attribute: "initial",
                        value: Number(event.target.value),
                      });
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={2}>
                <Input
                  data-cy="warehouseMapColumnPrefix"
                  placeholder={"-"}
                  disabled={isOnViewMode}
                  label={translate("prefix")}
                  value={mapColumns.prefix || ""}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapColumns,
                      attribute: "prefix",
                      value: event.target.value,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Input
                  data-cy="warehouseMapColumnSufix"
                  placeholder={"-"}
                  disabled={isOnViewMode}
                  label={translate("suffix")}
                  value={mapColumns.suffix || ""}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapColumns,
                      attribute: "suffix",
                      value: event.target.value,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  data-cy="warehouseMapColumnSequence"
                  placeholder={translate("select")}
                  label={translate("sequence")}
                  disabled={isOnViewMode}
                  value={mapColumns.ordination || ""}
                  onChange={(event: SelectChangeEvent<any>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapColumns,
                      attribute: "ordination",
                      value: event.target.value as AxisConfigOrdination,
                    });
                  }}
                >
                  {sequenceOrdination.map((ordination) => (
                    <MenuItem value={ordination.value} key={ordination.value}>
                      {ordination.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12}>
                <GridContainerTitle>{translate("rows")}</GridContainerTitle>
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  data-cy="warehouseMapRowQuantity"
                  placeholder={translate("input")}
                  label={translate("rowsQuantity")}
                  required
                  disabled={isOnViewMode}
                  type="number"
                  min={1}
                  error={fieldHasError("rowsQuantity")}
                  errorMessage={getErrorByField("rowsQuantity")}
                  value={mapRows.size}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapRows,
                      attribute: "size",
                      value: Number(event.target.value),
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  data-cy="warehouseMapRowCharacter"
                  placeholder={translate("select")}
                  label={translate("characterUsed")}
                  disabled={isOnViewMode}
                  value={mapRows.type || ""}
                  onChange={(event: SelectChangeEvent<any>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapRows,
                      attribute: "type",
                      value: event.target.value as AxisConfigType,
                    });
                    handleChangeColumnsType(event.target.value as AxisConfigType);
                  }}
                >
                  {charactersUsed.map((character) => (
                    <MenuItem value={character.value} key={character.value}>
                      {character.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} md={2}>
                {mapRows.type === AxisConfigType.ALPHABETICAL ? (
                  <Input
                    data-cy="warehouseMapRowInitial"
                    placeholder={"A"}
                    label={translate("initial")}
                    disabled={isOnViewMode}
                    value={getLetter(mapRows)}
                    type="text"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      hadleUpdateMapAxis({
                        attributeSetState: setMapRows,
                        attribute: "initial",
                        value: handleSetInitial(event.target.value),
                      });
                    }}
                  />
                ) : (
                  <Input
                    data-cy="warehouseMapRowInitial"
                    placeholder={"0"}
                    label={translate("initial")}
                    disabled={isOnViewMode}
                    value={mapRows.initial}
                    type="number"
                    min={0}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      hadleUpdateMapAxis({
                        attributeSetState: setMapRows,
                        attribute: "initial",
                        value: Number(event.target.value),
                      });
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={2}>
                <Input
                  data-cy="warehouseMapRowPrefix"
                  placeholder={"-"}
                  label={translate("prefix")}
                  disabled={isOnViewMode}
                  value={mapRows.prefix || ""}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapRows,
                      attribute: "prefix",
                      value: event.target.value,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Input
                  data-cy="warehouseMapRowSufix"
                  placeholder={"-"}
                  label={translate("suffix")}
                  disabled={isOnViewMode}
                  value={mapRows.suffix || ""}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapRows,
                      attribute: "suffix",
                      value: event.target.value,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  data-cy="warehouseMapRowSequence"
                  placeholder={translate("select")}
                  label={translate("sequence")}
                  disabled={isOnViewMode}
                  value={mapRows.ordination || ""}
                  onChange={(event: SelectChangeEvent<any>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapRows,
                      attribute: "ordination",
                      value: event.target.value as AxisConfigOrdination,
                    });
                  }}
                >
                  {sequenceOrdination.map((ordination) => (
                    <MenuItem value={ordination.value} key={ordination.value}>
                      {ordination.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12}>
                <GridContainerTitle>{translate("levels")}</GridContainerTitle>
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  data-cy="warehouseMapLevelQuantity"
                  placeholder={translate("input")}
                  label={translate("levelsQuantity")}
                  disabled={isOnViewMode}
                  required
                  type="number"
                  min={1}
                  error={fieldHasError("levelsQuantity")}
                  errorMessage={getErrorByField("levelsQuantity")}
                  value={mapLevels.size}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapLevels,
                      attribute: "size",
                      value: Number(event.target.value),
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  data-cy="warehouseMapLevelCharacter"
                  placeholder={translate("select")}
                  label={translate("characterUsed")}
                  disabled={isOnViewMode}
                  value={mapLevels.type || ""}
                  onChange={(event: SelectChangeEvent<any>) => {
                    hadleUpdateMapAxis({
                      attributeSetState: setMapLevels,
                      attribute: "type",
                      value: event.target.value as AxisConfigType,
                    });
                  }}
                >
                  {charactersUsed.map((character) => (
                    <MenuItem value={character.value} key={character.value}>
                      {character.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12}>
                {showWarehouseMap && (
                  <Button
                    customColor={Color.TEXT_INFO}
                    className={classes.resetButton}
                    onClick={() => {
                      setMatrixLengths(initialMatrixLengths);
                      setMapColumns(initialColumnConfig);
                      setMapRows(initialRowConfig);
                      setMapLevels(initialLevelConfig);
                      setShowWarehouseMap(false);
                      setHasOverride(false);
                      setMatrixValues([]);
                      setOverrideNames([]);
                    }}
                  >
                    {translate("reset")}
                  </Button>
                )}
                <Button
                  customColor={Color.SECONDARY}
                  disabled={!mapColumns.size || !mapRows.size || !mapLevels.size || isOnViewMode}
                  onClick={() => {
                    const building = buildingMapCells({
                      configColumn: mapColumns,
                      configRow: mapRows,
                      configLevel: mapLevels,
                    });

                    setCreatedMapCells(building?.mapCells);
                    setMatrixValues(building?.matrixValues);
                    setMatrixLengths(building?.matrixLengths);
                    setShowWarehouseMap(true);
                    setHasOverride(false);
                    setIsMapBuilded(true);
                  }}
                >
                  {translate("buildMap")}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <MapGeneratedWrapper container item direction="column" xs={6}>
            <MapGeneratedContainer>
              <GridContainerTitle>{translate("mapGenerated")}</GridContainerTitle>
              {showWarehouseMap && (
                <WarehouseMapComponent
                  matrixLengths={matrixLengths}
                  matrixValues={matrixValues}
                  overrideNames={overrideNames}
                  setHasOverride={setHasOverride}
                  setOverrideNames={setOverrideNames}
                  isViewMode={isOnViewMode}
                  isCreate={isCreatingNewMap}
                  mapCells={createdMapCells}
                  handleClickMapCell={(mapCell: MapCell) => {
                    setMapCellSelected(mapCell);
                    setShowLocationModal(true);
                  }}
                  setHasChangedMapProperty={setHasChangedMapProperty}
                />
              )}
            </MapGeneratedContainer>
          </MapGeneratedWrapper>
          {showLocationModal && (
            <WarehouseMapLocationModal
              open={showLocationModal}
              closeModal={() => setShowLocationModal(false)}
              createdMapCells={createdMapCells}
              levelsSize={mapLevels.size}
              mapCellSelected={mapCellSelected}
              onCreate={(mapCells) => {
                setCreatedMapCells(mapCells);
              }}
            />
          )}
        </Grid>
      )}
    </Modal>
  );
};
